import "react-big-calendar/lib/css/react-big-calendar.css";

import { useCallback, useEffect, useState, memo } from "react";

import MODALS from "../constants/modals";

import axios from "axios";
import moment from "moment";
import _ from "lodash";
import { useAppDispatch } from "../hooks/redux";
import { openModal } from "../features/modals";

import {
  Grid,
  Typography,
  Stack,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";

import { Calendar, Event, momentLocalizer } from "react-big-calendar";
import { usePermissions } from "../hooks/permissions";
import PERMISSIONS from "../constants/permissions";
import CustomToolbar from "../components/CalendarCustomToolbar";
import { IApartmentResponse } from "../types/classifiers";
import { useParams } from "react-router";
import { IApartmentReservationResponse } from "../types/apartmentReservation";
import ColorHash from "color-hash";
import ExportDropdown from "../components/ExportDropdown";
import { EXPORT_FORMATS } from "../types/export";

import writeXlsxFile from "write-excel-file";
import type { SheetData } from "write-excel-file";

import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import {
  ContentTable,
  TDocumentDefinitions,
  Content,
} from "pdfmake/interfaces";

// @ts-ignore
pdfMake.vfs = pdfFonts.pdfMake.vfs;

// Move expensive objects outside component to prevent recreation
const colorHash = new ColorHash();
const localizer = momentLocalizer(moment);

// Helper function to check if reservation overlaps with a day
const reservationOverlapsDay = (
  reservation: Event,
  day: moment.Moment
): boolean => {
  return (
    moment(reservation.start).isSame(day, "day") ||
    moment(reservation.end).isSame(day, "day") ||
    (moment(reservation.start).isBefore(day, "day") &&
      moment(reservation.end).isAfter(day, "day"))
  );
};

// Helper function to generate export data
const generateExportData = (
  reservations: Event[],
  start: moment.Moment,
  end: moment.Moment
) => {
  const data: Array<{ date: string; employee: string }> = [];

  for (let m = start.clone(); m.isSameOrBefore(end); m.add(1, "day")) {
    const dayReservations = reservations.filter((r) =>
      reservationOverlapsDay(r, m)
    );

    data.push({
      date: m.format("L"),
      employee:
        dayReservations.length > 0
          ? dayReservations[0].resource.user.name
          : "brīvs",
    });
  }

  return data;
};

const ApartmentsSchedule: React.FunctionComponent = memo(() => {
  const { clinic } = useParams<{ clinic: string }>();

  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const [isExporting, setIsExporting] = useState(false);
  const [apartments, setApartments] = useState<IApartmentResponse[]>([]);
  const [selectedApartment, setSelectedApartment] =
    useState<IApartmentResponse>();
  const [reservations, setReservations] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState<Date>(moment().toDate());

  const loadData = useCallback(() => {
    if (!selectedApartment) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const source = axios.CancelToken.source();

    axios
      .get<IApartmentReservationResponse[]>(
        `/apartments/${selectedApartment._id}/reservations`,
        {
          params: { date },
          cancelToken: source.token,
        }
      )
      .then((res) => {
        const mappedReservations = res.data.map((r) => ({
          start: new Date(r.from),
          end: new Date(r.to),
          allDay: true,
          title: r.user.name,
          resource: r,
        }));
        setReservations(mappedReservations);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          console.error("Error loading reservations:", err);
          setError("Failed to load reservations");
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => source.cancel("Component unmounted");
  }, [date, selectedApartment]);

  useEffect(() => {
    const cleanup = loadData();
    return cleanup;
  }, [loadData]);

  useEffect(() => {
    if (!clinic) {
      return;
    }

    const source = axios.CancelToken.source();

    axios
      .get<IApartmentResponse[]>(`/classifiers/apartments/clinic/${clinic}`, {
        cancelToken: source.token,
      })
      .then((res) => {
        setApartments(res.data);
        if (res.data.length > 0 && !selectedApartment) {
          setSelectedApartment(res.data[0]);
        }
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          console.error("Error loading apartments:", err);
          setError("Failed to load apartments");
        }
      });

    return () => source.cancel("Component unmounted");
  }, [clinic, selectedApartment]);

  const onNavigate = useCallback((newDate: Date) => setDate(newDate), []);

  const onExport = useCallback(
    async (format: EXPORT_FORMATS) => {
      if (!selectedApartment) {
        return;
      }

      setIsExporting(true);

      try {
        const start = moment(date).startOf("month");
        const end = moment(date).endOf("month");
        const exportData = generateExportData(reservations, start, end);

        switch (format) {
          case EXPORT_FORMATS.XLSX:
            const columns = [{ width: 20 }, { width: 50 }];
            const data: SheetData = [
              [
                {
                  value: `${selectedApartment.name} - ${_.capitalize(
                    moment(date).format("MMMM YYYY")
                  )}`,
                  fontWeight: "bold",
                  fontSize: 16,
                  span: 2,
                  align: "center",
                },
                null,
              ],
              [
                { value: "Datums", fontWeight: "bold" },
                { value: "Darbinieks", fontWeight: "bold" },
              ],
            ];

            exportData.forEach(({ date, employee }) => {
              data.push([{ value: date }, { value: employee }]);
            });

            const sheetName = selectedApartment.name.replace(
              /[\[\]\/\\\:\*\?]/g,
              ""
            );
            const fileName = `${selectedApartment.name} ${moment(date)
              .startOf("month")
              .format("L")} - ${moment(date).endOf("month").format("L")}`;

            await writeXlsxFile(data, {
              columns,
              fileName: fileName + ".xlsx",
              sheet: sheetName,
            });
            break;

          case EXPORT_FORMATS.PDF:
            const docDefinition: TDocumentDefinitions = {
              content: [
                {
                  text: `${selectedApartment.name} - ${_.capitalize(
                    moment(date).format("MMMM YYYY")
                  )}`,
                  style: "header",
                  alignment: "center",
                },
              ],
              styles: {
                header: {
                  fontSize: 16,
                  bold: true,
                  margin: [0, 0, 0, 20],
                },
                tableExample: {
                  margin: [0, 5, 0, 15],
                },
                tableHeader: {
                  bold: true,
                  fontSize: 12,
                  color: "black",
                  fillColor: "#f0f0f0",
                },
              },
            };

            const contentTable: ContentTable = {
              style: "tableExample",
              table: {
                widths: ["30%", "70%"],
                body: [
                  [
                    { text: "Datums", style: "tableHeader" },
                    { text: "Darbinieks", style: "tableHeader" },
                  ],
                  ...exportData.map(({ date, employee }) => [
                    { text: date },
                    { text: employee },
                  ]),
                ],
              },
            };

            (docDefinition.content as Content[]).push(contentTable);

            const pdfFileName = `${selectedApartment.name} ${moment(date)
              .startOf("month")
              .format("L")} - ${moment(date).endOf("month").format("L")}`;

            pdfMake.createPdf(docDefinition).download(pdfFileName + ".pdf");
            break;
        }
      } catch (error) {
        console.error("Export failed:", error);
        setError("Export failed");
      } finally {
        setIsExporting(false);
      }
    },
    [reservations, selectedApartment, date]
  );

  const eventPropGetter = useCallback(
    (event: Event) => ({
      style: {
        cursor: "pointer",
        backgroundColor: colorHash.hex(event.resource.user._id),
      },
    }),
    []
  );

  const onSelectEvent = useCallback(
    (event: Event) => {
      if (!selectedApartment) {
        return;
      }

      dispatch(
        openModal({
          type: MODALS.ReserveApartment,
          apartment: selectedApartment,
          clinic: clinic!,
          initialValues: event.resource,
          callback: loadData,
        })
      );
    },
    [selectedApartment, clinic, dispatch, loadData]
  );

  const handleApartmentChange = useCallback(
    (_: any, value: IApartmentResponse | null) => {
      setSelectedApartment(value || undefined);
    },
    []
  );

  const onSelectSlot = useCallback(
    (slotInfo: { start: Date; end: Date }) => {
      if (
        !selectedApartment ||
        !hasPermission(PERMISSIONS.WRITE_RESERVE_APARTMENTS)
      ) {
        return;
      }

      dispatch(
        openModal({
          type: MODALS.ReserveApartment,
          apartment: selectedApartment,
          clinic: clinic!,
          initialValues: {
            from: slotInfo.start,
            to: moment(slotInfo.end).subtract(1, "day").endOf("day").toDate(),
          },
          callback: loadData,
        })
      );
    },
    [selectedApartment, hasPermission, clinic, dispatch, loadData]
  );

  if (!clinic) {
    return null;
  }

  if (error) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4" color="error">
          Error: {error}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h4">Dienesta dzīvokļu grafiks</Typography>

      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item xs>
          <Autocomplete
            options={apartments}
            style={{ width: 300 }}
            value={selectedApartment || null}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderInput={(params) => (
              <TextField {...params} label="Dienesta dzīvoklis" />
            )}
            onChange={handleApartmentChange}
            size="small"
          />
        </Grid>
        <Grid item>
          <ExportDropdown
            loading={isExporting}
            supportedFormats={[EXPORT_FORMATS.XLSX, EXPORT_FORMATS.PDF]}
            onExport={onExport}
          />
        </Grid>
      </Grid>
      <Box sx={{ height: 800 }}>
        <Calendar
          date={date}
          events={reservations}
          localizer={localizer}
          views={["month", "week"]}
          drilldownView={null}
          onNavigate={onNavigate}
          onSelectEvent={onSelectEvent}
          onSelectSlot={onSelectSlot}
          selectable
          popup
          eventPropGetter={eventPropGetter}
          components={{
            toolbar: CustomToolbar,
          }}
        />
      </Box>
    </Stack>
  );
});

ApartmentsSchedule.displayName = "ApartmentsSchedule";

export default ApartmentsSchedule;
